'use client';
import { OrderDetailsFeature } from '@features/order-details';
import { Game } from '@utils/AppConfig';
import {
  TableActiveOrdersRows,
  TablePurchasesRows,
  TableWithdrawRows,
} from '@widgets/desktop/profile-history/ui/profile-history.types';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import React from 'react';

import { formatDateToDDMMYYYY } from '@/mocks/profile-history-tables';
import { WithCurrency } from '@/shared/hocs';
import {
  DivWithChildrenProps,
  ItemAge,
  ItemMM2RarityVariants,
  ItemRarityVariants,
  Product,
} from '@/shared/types/common';
import {
  Illustration,
  Item,
  ItemRarityCard,
  TagCategory,
  Typography,
} from '@/shared/ui';
import {
  colorsByVariant,
  colorsMM2Variant,
} from '@/shared/ui/item-rarity-card/item-rarity-card';
import { useStore } from '@/store/context';

import styles from './card-history.module.scss';
import { TagInfoProps, TagInfoTextProps } from './card-history.types';

const statuses = {
  FAILED: 'danger',
  PENDING: 'warning',
  COMPLETED: 'success',
  PAID: 'success',
  PLACED: 'warning',
  'READY FOR WITHDRAW': 'success',
};

export const CardHistoryEntityMobile = ({
  children,
  className,
  ...props
}: DivWithChildrenProps) => {
  return (
    <div className={cn(styles['container'], className)} {...props}>
      {children}
    </div>
  );
};

// eslint-disable-next-line react/display-name
CardHistoryEntityMobile.Purchase = observer(
  ({
    children,
    row,
    className,
    ...props
  }: DivWithChildrenProps & { row: TablePurchasesRows }) => {
    const notificationStore = useStore()?.notifications;
    const handleCopyId = () => {
      navigator.clipboard
        .writeText(String(row.id))
        .then(() => {
          notificationStore?.addNotification({
            variant: 'success',
            title: 'Copied!',
            subtitle: `ID: ${String(row.id)}`,
            delay: 3,
            id: '1-copy',
          });
        })
        .catch(() => {
          notificationStore?.addNotification({
            variant: 'error',
            title: 'Something went wrong...',
            subtitle: 'Unable to copy',
            delay: 3,
            id: '2-copy',
          });
        });
    };

    return (
      <div className={cn(styles['container'], className)} {...props}>
        <Slot>
          <HistoryTag>
            {/* <HistoryTag.Text>{row.type}</HistoryTag.Text>*/}
          </HistoryTag>

          <HistoryTag>
            {/*<HistoryTag.Text>{row.date}</HistoryTag.Text>*/}
          </HistoryTag>
        </Slot>

        <Slot>
          <ItemContent>
            <ItemContent.ItemAndAge>
              {/* <ItemContent.Item
                src={row.item.imageUri}
                rare={row.item.rare}
                game={row.item.game}
              />
              <ItemContent.Info
                info={{
                  title: row.item.name,
                  age: row.item.age,
                }}
              />*/}
            </ItemContent.ItemAndAge>
          </ItemContent>
          {/* <ItemContent.Properties
            flyable={row.properties.flyable}
            rideable={row.properties.rideable}
            pumping={row.properties.pumping}
          />*/}
        </Slot>

        <Slot>
          <HistoryTag>
            {/* <HistoryTag.Text>$ {row.price}</HistoryTag.Text>*/}
          </HistoryTag>
          <HistoryTag onClick={() => handleCopyId()}>
            <HistoryTag.Text>ID: {row.id}</HistoryTag.Text>
            <Illustration
              name={'copy'}
              size={16}
              spriteName={'icons'}
              style={{ color: '#BFBFBF' }}
            />
          </HistoryTag>
        </Slot>
      </div>
    );
  },
);

// eslint-disable-next-line react/display-name
CardHistoryEntityMobile.Withdraw = observer(
  ({
    children,
    row,
    className,
    ...props
  }: DivWithChildrenProps & { row: TableWithdrawRows }) => {
    const variant = statuses[
      row?.status.toUpperCase() as keyof typeof statuses
    ] as 'default' | 'success' | 'warning' | 'danger';

    const notificationStore = useStore()?.notifications;
    const handleCopyId = () => {
      navigator.clipboard
        .writeText(String(row.id))
        .then(() => {
          notificationStore?.addNotification({
            variant: 'success',
            title: 'Copied!',
            subtitle: `ID: ${String(row.id)}`,
            delay: 3,
            id: '1-copy',
          });
        })
        .catch(() => {
          notificationStore?.addNotification({
            variant: 'error',
            title: 'Something went wrong!',
            subtitle: 'Unable to copy',
            delay: 3,
            id: '2-copy',
          });
        });
    };

    function toSentenceCase(str: string) {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    return (
      <div className={cn(styles['container'], className)} {...props}>
        <Slot>
          <HistoryTag>
            <HistoryTag.Text>Nick: {row.nickname}</HistoryTag.Text>
          </HistoryTag>

          <HistoryTag variant={variant}>
            <HistoryTag.Text variant={variant}>
              {toSentenceCase(row.status?.toLowerCase())}
            </HistoryTag.Text>
          </HistoryTag>
        </Slot>

        <Slot>
          <ItemContent>
            <ItemContent.ItemAndAge>
              <ItemContent.Item
                src={row.item.imageUri}
                rare={row.item.rare}
                game={row.item.game}
              />
              <ItemContent.Info
                info={{
                  title: row.item.name,
                  age: row.item.age,
                }}
              />
            </ItemContent.ItemAndAge>
          </ItemContent>
          <ItemContent.Properties
            flyable={row.properties.flyable}
            rideable={row.properties.rideable}
            pumping={row.properties.pumping}
          />
        </Slot>

        <Slot>
          <HistoryTag>
            <HistoryTag.Text>{row.date}</HistoryTag.Text>
          </HistoryTag>

          <HistoryTag width={'content'}>
            <HistoryTag.Text>$ {row.price}</HistoryTag.Text>
          </HistoryTag>

          <HistoryTag onClick={() => handleCopyId()}>
            <HistoryTag.Text>ID: {row.id}</HistoryTag.Text>
            <Illustration
              name={'copy'}
              size={16}
              spriteName={'icons'}
              style={{ color: '#BFBFBF' }}
            />
          </HistoryTag>
        </Slot>
      </div>
    );
  },
);

// eslint-disable-next-line react/display-name
CardHistoryEntityMobile.Orders = observer(
  ({
    children,
    row,
    className,
    orders,
    ...props
  }: DivWithChildrenProps & {
    row: TableActiveOrdersRows;
    orders: 'purchases' | 'active-orders';
  }) => {
    const notificationStore = useStore()?.notifications;
    const handleCopyId = () => {
      navigator.clipboard
        .writeText(String(row.id))
        .then(() => {
          notificationStore?.addNotification({
            variant: 'success',
            title: 'Copied!',
            subtitle: `ID: ${String(row.id)}`,
            delay: 3,
            id: '1-copy',
          });
        })
        .catch(() => {
          notificationStore?.addNotification({
            variant: 'error',
            title: 'Something went wrong!',
            subtitle: 'Unable to copy',
            delay: 3,
            id: '2-copy',
          });
        });
    };

    const variant = statuses[
      row?.status.toUpperCase() as keyof typeof statuses
    ] as 'default' | 'success' | 'warning' | 'danger';

    const availablePaymentMethods =
      useStore()?.cart?.checkoutInfo?.availablePaymentMethods;
    const methods = availablePaymentMethods;

    const paymentMethod = methods?.find(
      item => item.id === row?.paymentMethodId,
    );

    return (
      <div className={cn(styles['container'], className)} {...props}>
        <div className={styles['slot']}>
          <div className={styles['price']}>
            <Typography className={styles['price-info']}>
              <WithCurrency>{row?.totalAmount}</WithCurrency>
            </Typography>
            <div className={styles['date']}>
              <Typography className={styles['date-info']}>
                {formatDateToDDMMYYYY(row?.createdAt)}
              </Typography>
              <Typography className={styles['date-info-sub']}>
                3:50 PM
              </Typography>
            </div>
          </div>

          <div className={styles['method']}>
            <div className={styles['payment-method']}>
              {paymentMethod && (
                <Image
                  draggable={false}
                  src={paymentMethod?.imgUrl || ''}
                  quality={75}
                  priority={true}
                  loading="eager"
                  width={67}
                  height={32}
                  alt={`${paymentMethod?.provider} payment method`}
                  className={`${styles['payment-method-image']}`}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles['slot']}>
          <HistoryTag variant={variant}>
            <HistoryTag.Text variant={variant}>
              {row.status?.toUpperCase()}
            </HistoryTag.Text>
          </HistoryTag>
          <HistoryTag onClick={() => handleCopyId()}>
            <HistoryTag.Text>ID {row.id?.slice(0, 5)}</HistoryTag.Text>
            <Illustration
              name={'copy'}
              size={16}
              spriteName={'icons'}
              style={{ color: '#BFBFBF' }}
            />
          </HistoryTag>
        </div>
        <div className={styles['slot']}>
          <OrderDetailsFeature isMobile item={row} orders={orders} />
        </div>
      </div>
    );
  },
);

const ItemContent = ({
  children,
  className,
  ...props
}: DivWithChildrenProps) => {
  return (
    <div className={cn(styles['item-content'], className)} {...props}>
      {children}
    </div>
  );
};

const Slot = ({ children }: DivWithChildrenProps) => {
  return <div className={styles['slot']}>{children}</div>;
};

// eslint-disable-next-line react/display-name
ItemContent.Item = ({
  src,
  rare,
  game,
}: {
  src: string;
  rare: ItemRarityVariants | ItemMM2RarityVariants;
  game: Game;
}) => {
  const colorScheme = game === 'mm2' ? colorsMM2Variant : colorsByVariant;

  return (
    <div className={styles['item-container']}>
      <ItemRarityCard
        colorScheme={colorScheme}
        className={styles['background']}
        size={'50'}
        variant={rare}
      />
      <Item className={styles['item']} size={'42'} src={src} />
    </div>
  );
};
// eslint-disable-next-line react/display-name
ItemContent.ItemAndAge = ({ children }: DivWithChildrenProps) => {
  return <div className={styles['item-age']}>{children}</div>;
};

// eslint-disable-next-line react/display-name
ItemContent.Properties = ({
  flyable,
  rideable,
  pumping,
}: Pick<Product, 'flyable' | 'rideable' | 'pumping'>) => {
  return (
    <div className={styles['properties']}>
      {pumping && <TagCategory variant={pumping} />}
      {flyable && <TagCategory variant={'fly'} />}
      {rideable && <TagCategory variant={'ride'} />}
    </div>
  );
};
// eslint-disable-next-line react/display-name
ItemContent.Info = ({ info }: { info: { title: string; age?: ItemAge } }) => {
  return (
    <div className={styles['item-info']}>
      <Typography className={styles['title']}>{info.title}</Typography>
      {info?.age && (
        <Typography className={styles['age']}>
          {info?.age?.split('_').join(' ')}
        </Typography>
      )}
    </div>
  );
};

const HistoryTag = ({
  children,
  width = 'full-w',
  className,
  variant = 'default',
  ...props
}: TagInfoProps) => {
  const tagInfoClasses = cn(
    styles['tag-info'],
    styles[`tag-info--${width}`],
    styles[`tag-info-variant--${variant}`],
    className,
  );

  return (
    <div className={tagInfoClasses} {...props}>
      {children}
    </div>
  );
};

// eslint-disable-next-line react/display-name
HistoryTag.Text = ({
  children,
  className,
  variant = 'default',
}: TagInfoTextProps) => {
  return (
    <Typography
      className={cn(
        styles['text'],
        styles[`text-variant--${variant}`],
        className,
      )}
    >
      {children}
    </Typography>
  );
};
